import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  RouteComponentProps,
  StaticContext,
  withRouter,
  useParams,
  useLocation,
} from 'react-router';
import { ModifiersUtils } from 'react-day-picker';
import Title from '../components/Title';
import {
  Address,
  validateAddress,
  getCityFromPostcode,
} from '../components/AddressLookup/address';
// The only address picker. v1, the selector that chose between them and the
// ?address-picker=legacy flag were deleted on 2026-09-26 — four of the five
// surfaces rendering v1 bypassed that flag anyway.
import AddressLookup from '../components/AddressLookup/AddressLookupV2';
import 'react-day-picker/lib/style.css';
import {
  BoxData,
  Product,
  Dimensions,
  BookingParams,
} from '../models/APIModels';
import Button from '../components/Button';
import BookingSummary, {
  SummaryArrow,
  SummaryFigure,
} from '../components/BookingSummary';
import BookingLeg from '../components/booking/BookingLeg';
import DateField from '../components/booking/DateField';
import Callout from '../components/Callout';
import NavigationStatus from '../components/NavigationStatus';
import useGtmEventTracking from '../components/useGtmEventTracking';
import { useDateDisabler } from '../utilities/DateDisabler';
import { MIN_STORAGE_WORKING_DAYS } from '../utilities/CollectionDateConstraints';
import { isOutOfArea, isDifferentArea } from '../utilities/Postcode';
import {
  toMoney,
  monthlyCostToMoney,
  orderCostToMoney,
  purchaseCostToMoney,
  suitcaseOrderToMoney,
} from '../utilities/MoneyConversion';
import {
  COLLECTION_FEE,
  COLLECTION_SURCHARGE,
  REDELIVER_FEE,
  REDELIVER_SURCHARGE,
  REMOVALS_SERVICE_CITIES,
} from '../constants';
import { useFlow } from '../utilities/useFlow';

interface CollectionDeliveryProps
  extends RouteComponentProps<undefined, StaticContext, CollectionState> {}

interface CollectionState {
  boxes: BoxData[];
  collectionAddress?: Address;
  collectionDate?: Date;
  deliveryAddress?: Address;
  deliveryDate?: Date;
  storageRequired: boolean;
  suitcases?: Dimensions[];
  suitcaseData?: Product;
  dontKnowAddress?: boolean;
  selectedTier?: any;
  questions?: any;
  removalService?: boolean;
  moveDate?: Date;
  wantsStorage?: boolean;
}

type AddressIssues = Partial<Record<keyof Address, string>>;

/**
 * What is missing from an address, field by field.
 *
 * `validateAddress` answers one boolean for the whole thing, which is all the
 * submit gate needs but is useless to the customer: it cannot say whether the
 * postcode is wrong or the first line is empty. Same rule, reported per field.
 */
function addressIssues(address: Address): AddressIssues {
  const issues: AddressIssues = {};

  if (!address.line1 || !address.line1.trim()) {
    issues.line1 = 'Enter the first line of the address.';
  }

  if (!address.postCode || !address.postCode.trim()) {
    issues.postCode = 'Enter a postcode.';
  } else if (!validateAddress({ ...address, line1: address.line1 || 'x' })) {
    issues.postCode = 'Enter a valid UK postcode, for example SW1A 1AA.';
  }

  return issues;
}

function hasIssues(issues: AddressIssues): boolean {
  return Object.keys(issues).length > 0;
}

/** "a, b and c" — a list a person would read out. */
function listSentence(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

function CollectionDeliveryComponent({
  history,
}: CollectionDeliveryProps): JSX.Element {
  const location = useLocation();
  const { route } = useParams<BookingParams>();

  const locationState = history.location.state || {};

  const getStorageKey = () => {
    if (location.pathname.includes('/removals')) return 'removals-state';
    if (location.pathname.includes('/storage')) return 'storage-state';
    if (location.pathname.includes('/shipping')) return 'shipping-state';
    if (location.pathname.includes('/suitcase')) return 'suitcase-state';
    return 'collection-delivery-state';
  };

  const storageKey = getStorageKey();

  const localStorageState = (() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      return {};
    }
  })();

  const combinedState = { ...localStorageState, ...locationState };

  const {
    boxes,
    storageRequired,
    suitcases,
    suitcaseData,
    selectedTier,
    questions,
    removalService,
    moveDate: initialMoveDate,
    wantsStorage,
    collectionAddress: initialCollectionAddress,
    collectionDate: initialCollectionDate,
    deliveryAddress: initialDeliveryAddress,
    deliveryDate: initialDeliveryDate,
    dontKnowAddress: initialDontKnowAddress,
  } = combinedState;

  const parseDate = (date: any): Date | undefined => {
    if (!date) return undefined;
    if (date instanceof Date) return date;
    if (typeof date === 'string' || typeof date === 'number') {
      const parsed = new Date(date);
      return isNaN(parsed.getTime()) ? undefined : parsed;
    }
    return undefined;
  };

  const flow = useFlow();

  const stAnnesDefaultAddress: Address = {
    line1: "St Anne's College",
    line2: '56 Woodstock Rd',
    city: 'Oxford',
    county: 'Oxfordshire',
    postCode: 'OX2 6HS',
    country: 'United Kingdom',
  };
  const stAnnesDefaultDate = new Date(2026, 2, 13); // Friday 13th March 2026

  const isStAnnesWithNoAddress =
    flow === 'St Annes' && !initialCollectionAddress?.postCode;

  const isRemovalsFlow =
    location.pathname.includes('/removals') || removalService;

  const [collectionAddress, setCollectionAddressState] = useState<Address>(
    isStAnnesWithNoAddress
      ? stAnnesDefaultAddress
      : initialCollectionAddress || {},
  );
  const [collectionDate, setCollectionDate] = useState<Date | undefined>(
    isStAnnesWithNoAddress
      ? stAnnesDefaultDate
      : parseDate(initialCollectionDate),
  );
  const [deliveryAddress, setDeliveryAddressState] = useState<Address>(
    initialDeliveryAddress || {},
  );
  const [deliveryDate, setDeliveryDate] = useState<Date | undefined>(
    parseDate(initialDeliveryDate),
  );
  const [dontKnowDeliveryAddress, setDontKnowDeliveryAddress] =
    useState<boolean>(initialDontKnowAddress || false);
  const [moveDate, setMoveDate] = useState<Date | undefined>(
    parseDate(initialMoveDate),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Whether the customer wants to give a return address now. Starts on when
  // there is already an answer in it — coming back from Payment, or reloading
  // a saved booking. Switching it off does not erase what they typed; it just
  // stops the booking carrying it, so switching back on restores the answer.
  const [isReturnOn, setIsReturnOn] = useState<boolean>(
    !!(initialDeliveryAddress?.postCode || initialDeliveryAddress?.line1),
  );
  // Errors are computed from state on every render, but only *shown* once the
  // customer has tried to go on. Validating a field the moment it is touched
  // tells someone half-way through typing a postcode that it is wrong.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // The return address on the storage flow is genuinely optional: blank is a
  // valid answer. It only has to be complete once the customer starts it.
  const deliveryStarted = !!(deliveryAddress.postCode || deliveryAddress.line1);
  // What the booking actually carries. The switch is authoritative — an
  // address typed and then switched off is not part of the order, which is
  // what "we will tell you later" has to mean for the summary to be honest.
  const returnProvided = !isRemovalsFlow && storageRequired
    ? isReturnOn && !!deliveryAddress.postCode
    : !!deliveryAddress.postCode;

  useEffect(() => {
    const stateToSave = {
      boxes,
      collectionAddress,
      collectionDate,
      deliveryAddress: isRemovalsFlow
        ? dontKnowDeliveryAddress
          ? {}
          : deliveryAddress
        : returnProvided
          ? deliveryAddress
          : {},
      deliveryDate: returnProvided || isRemovalsFlow ? deliveryDate : undefined,
      dontKnowAddress: isRemovalsFlow
        ? dontKnowDeliveryAddress
        : !returnProvided,
      storageRequired,
      suitcases,
      suitcaseData,
      selectedTier,
      questions,
      removalService,
      moveDate,
      wantsStorage,
    };

    try {
      localStorage.setItem(storageKey, JSON.stringify(stateToSave));
      localStorage.setItem('payment-state', JSON.stringify(stateToSave));
    } catch (e) {
      // Silently fail if localStorage is not available
    }
  }, [
    collectionAddress,
    collectionDate,
    deliveryAddress,
    deliveryDate,
    dontKnowDeliveryAddress,
    returnProvided,
    moveDate,
    storageKey,
    boxes,
    storageRequired,
    suitcases,
    suitcaseData,
    selectedTier,
    questions,
    removalService,
    wantsStorage,
    isRemovalsFlow,
  ]);

  const setCollectionAddress = (address: Address) => {
    setCollectionAddressState(address);
  };

  const setDeliveryAddress = (address: Address) => {
    setDeliveryAddressState(address);
  };

  // Messages raised by the picker itself when a typed date lands on a day the
  // calendar has disabled.
  const [collectionDateError, setCollectionDateError] = useState<string | null>(
    null,
  );
  const [deliveryDateError, setDeliveryDateError] = useState<string | null>(
    null,
  );
  const [moveDateError, setMoveDateError] = useState<string | null>(null);
  const [differentCityError, setDifferentCityError] = useState<string | null>(
    null,
  );

  const collectionDisabledDays = useDateDisabler(null, null);
  const deliveryDisabledDays = useDateDisabler(
    deliveryAddress,
    collectionDate ?? null,
    undefined,
    MIN_STORAGE_WORKING_DAYS,
  );

  // For removals, use tier-based buffer: 3 days for Mini/Small, 7 days for others
  const getMoveDateBuffer = () => {
    if (!isRemovalsFlow) return undefined;
    if (selectedTier) {
      const tierName = selectedTier.name?.toLowerCase() || '';
      if (tierName.includes('mini') || tierName.includes('small')) {
        return 3;
      }
    }
    return 7;
  };
  const baseMoveDateDisabledDays = useDateDisabler(
    null,
    null,
    getMoveDateBuffer(),
  );

  const isMiniOrSmallTier = !!(
    selectedTier &&
    (selectedTier.name?.toLowerCase().includes('mini') ||
      selectedTier.name?.toLowerCase().includes('small'))
  );

  // For Mini/Small tiers in Cambridge or London, enable Saturdays
  const moveDisabledDays = React.useMemo(() => {
    if (!isRemovalsFlow || !selectedTier) return baseMoveDateDisabledDays;

    const tierName = selectedTier.name?.toLowerCase() || '';
    const isMiniOrSmall =
      tierName.includes('mini') || tierName.includes('small');
    const collectionCity = collectionAddress.postCode
      ? getCityFromPostcode(collectionAddress.postCode)
      : '';
    const isCambridgeOrLondon =
      collectionCity === 'Cambridge' || collectionCity === 'London';

    // If Mini/Small and in Cambridge or London, remove Saturday restriction
    if (isMiniOrSmall && isCambridgeOrLondon) {
      return baseMoveDateDisabledDays
        .filter((day: any) => {
          // Keep all disabled days except the one that disables Saturdays
          if (day.daysOfWeek && Array.isArray(day.daysOfWeek)) {
            // Only disable Sundays (0), not Saturdays (6)
            return false;
          }
          return true;
        })
        .concat([{ daysOfWeek: [0] }]); // Only disable Sundays
    }

    return baseMoveDateDisabledDays;
  }, [
    isRemovalsFlow,
    selectedTier,
    collectionAddress.postCode,
    baseMoveDateDisabledDays,
  ]);

  // ── Validity ────────────────────────────────────────────────────────────
  // The gate booleans are unchanged; what is new is that each one now has a
  // matching per-field message, so a customer who cannot go on is told why.

  const collectionAddressIssues = addressIssues(collectionAddress);

  // Removals asks for the collection address only; the date is asked once, as
  // the move date, further down.
  const collectionDateIssue = isRemovalsFlow
    ? null
    : !collectionDate
      ? 'Choose a collection date.'
      : collectionDateError ||
        (ModifiersUtils.dayMatchesModifier(collectionDate, collectionDisabledDays)
          ? 'That date is not available. Please choose another.'
          : null);

  const isCollectionValid = isRemovalsFlow
    ? !hasIssues(collectionAddressIssues)
    : !hasIssues(collectionAddressIssues) && !collectionDateIssue;

  const deliveryRequired = isRemovalsFlow
    ? !wantsStorage || !dontKnowDeliveryAddress
    : !storageRequired;

  const deliveryAddressIssues =
    deliveryRequired || (storageRequired && isReturnOn && deliveryStarted)
      ? addressIssues(deliveryAddress)
      : {};

  const deliveryDateIssue =
    !storageRequired || isRemovalsFlow || !isReturnOn || !deliveryDate
      ? null
      : deliveryDateError ||
        (ModifiersUtils.dayMatchesModifier(deliveryDate, deliveryDisabledDays)
          ? 'That date is not available. Please choose another.'
          : null);

  const isDeliveryValid =
    !hasIssues(deliveryAddressIssues) && !deliveryDateIssue;

  const moveDateIssue = !isRemovalsFlow
    ? null
    : !moveDate
      ? 'Choose the day you would like to move.'
      : moveDateError ||
        (ModifiersUtils.dayMatchesModifier(moveDate, moveDisabledDays)
          ? 'That date is not available. Please choose another.'
          : null);

  const isMoveDateValid = !moveDateIssue;

  const isValid =
    isCollectionValid &&
    isDeliveryValid &&
    isMoveDateValid &&
    !differentCityError;

  // Check if delivery city is different from collection city (for removals)
  useEffect(() => {
    if (
      isRemovalsFlow &&
      collectionAddress.postCode &&
      deliveryAddress.postCode &&
      !dontKnowDeliveryAddress
    ) {
      const collectionCity = getCityFromPostcode(collectionAddress.postCode);
      const deliveryCity = getCityFromPostcode(deliveryAddress.postCode);

      if (collectionCity && deliveryCity && collectionCity !== deliveryCity) {
        setDifferentCityError(
          "It looks like you're moving to a different city, which needs a bespoke quote. Please contact us and we'll price it for you.",
        );
      } else {
        setDifferentCityError(null);
      }
    } else {
      setDifferentCityError(null);
    }
  }, [
    isRemovalsFlow,
    collectionAddress.postCode,
    deliveryAddress.postCode,
    dontKnowDeliveryAddress,
  ]);


  const gtmEventTracking = useGtmEventTracking();
  useEffect(() => {
    gtmEventTracking({ event: 'booking_step_collection_delivery_address' });
    window.scrollTo(0, 0);
  }, [gtmEventTracking]);

  // ── What is still outstanding ───────────────────────────────────────────
  // One list, in the order the page asks for things, feeding both the summary
  // line and the focus target on a failed submit.
  const outstanding = useMemo(() => {
    const items: { label: string; fieldId: string; inReturnLeg: boolean }[] = [];

    if (collectionAddressIssues.line1 || collectionAddressIssues.postCode) {
      items.push({
        label: isRemovalsFlow
          ? 'the address you are moving from'
          : 'the collection address',
        fieldId: collectionAddressIssues.line1
          ? 'collection-line1'
          : 'collection-postCode',
        inReturnLeg: false,
      });
    }
    if (collectionDateIssue) {
      items.push({
        label: 'a collection date',
        fieldId: 'collectionDate',
        inReturnLeg: false,
      });
    }
    if (deliveryAddressIssues.line1 || deliveryAddressIssues.postCode) {
      items.push({
        label: isRemovalsFlow
          ? 'the address you are moving to'
          : storageRequired
            ? 'the rest of the return address'
            : 'the delivery address',
        fieldId: deliveryAddressIssues.line1
          ? 'delivery-line1'
          : 'delivery-postCode',
        inReturnLeg: true,
      });
    }
    if (deliveryDateIssue) {
      items.push({
        label: 'a return date we can make',
        fieldId: 'deliveryDate',
        inReturnLeg: true,
      });
    }
    if (moveDateIssue) {
      items.push({
        label: 'a move date',
        fieldId: 'moveDate',
        inReturnLeg: false,
      });
    }
    return items;
  }, [
    collectionAddressIssues.line1,
    collectionAddressIssues.postCode,
    collectionDateIssue,
    deliveryAddressIssues.line1,
    deliveryAddressIssues.postCode,
    deliveryDateIssue,
    moveDateIssue,
    isRemovalsFlow,
    storageRequired,
  ]);

  const handleSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      setSubmitAttempted(true);

      if (!isValid) {
        // Take the customer to the first thing that needs them, rather than
        // leaving a button that appears to do nothing. Opens the optional leg
        // first if that is where the problem is.
        const first = outstanding[0];
        if (first?.inReturnLeg) {
          setIsReturnOn(true);
        }
        window.setTimeout(() => {
          const target = first
            ? document.getElementById(first.fieldId)
            : null;
          if (target) {
            target.focus({ preventScroll: true });
            target.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }, 0);
        return;
      }

      setIsSubmitting(true);
      const nextRoute = isRemovalsFlow
        ? '/removals/payment'
        : `/${route}/payment`;
      history.push(nextRoute, {
        boxes,
        collectionAddress,
        collectionDate,
        deliveryAddress: isRemovalsFlow
          ? dontKnowDeliveryAddress
            ? {}
            : deliveryAddress
          : returnProvided
            ? deliveryAddress
            : {},
        deliveryDate:
          returnProvided || isRemovalsFlow ? deliveryDate : undefined,
        dontKnowAddress: isRemovalsFlow
          ? dontKnowDeliveryAddress
          : !returnProvided,
        storageRequired,
        suitcases,
        suitcaseData,
        selectedTier,
        questions,
        removalService,
        moveDate,
        wantsStorage,
      });
    },
    [
      isValid,
      outstanding,
      isRemovalsFlow,
      route,
      history,
      boxes,
      collectionAddress,
      collectionDate,
      deliveryAddress,
      deliveryDate,
      dontKnowDeliveryAddress,
      returnProvided,
      storageRequired,
      suitcases,
      suitcaseData,
      selectedTier,
      questions,
      removalService,
      moveDate,
      wantsStorage,
    ],
  );

  // Only surface messages once the customer has asked to go on.
  const shown = <T,>(value: T): T | undefined =>
    submitAttempted ? value : undefined;

  const collectionErrors = submitAttempted ? collectionAddressIssues : {};
  const deliveryErrors = submitAttempted ? deliveryAddressIssues : {};

  // ── Summary ─────────────────────────────────────────────────────────────
  const deliveryCostRequired = (boxes || []).some(
    (box: BoxData) =>
      box.name !== 'Suitcase' &&
      box.name !== 'Own box' &&
      box.name !== 'Suitcase / Own box',
  );

  const figures: SummaryFigure[] = useMemo(() => {
    if (isRemovalsFlow && selectedTier) {
      return [
        {
          label: 'Total',
          value: toMoney(selectedTier.price),
          headline: true,
        },
      ];
    }
    if (storageRequired) {
      return [
        {
          label: 'Materials',
          value: purchaseCostToMoney(boxes || []),
          headline: true,
        },
        {
          label: 'Monthly storage',
          value: monthlyCostToMoney(boxes || []),
          headline: true,
        },
      ];
    }
    return [
      {
        label: 'Order total',
        value: suitcases
          ? suitcaseOrderToMoney(suitcases.length, suitcaseData?.shippingCost)
          : orderCostToMoney(boxes || [], deliveryCostRequired),
        headline: true,
      },
    ];
  }, [
    isRemovalsFlow,
    selectedTier,
    storageRequired,
    boxes,
    suitcases,
    suitcaseData,
    deliveryCostRequired,
  ]);

  const status = isValid
    ? 'Ready to continue'
    : outstanding.length > 0
      ? `Still needed: ${listSentence(outstanding.map((item) => item.label))}.`
      : // Nothing is missing, but something is wrong: a move to a different
        // city cannot be priced from this page. Without this branch the
        // sentence rendered as "Still needed: ." — a list of nothing.
        'This move needs a bespoke quote — please contact us.';

  const pageTitle = isRemovalsFlow ? 'Your move' : 'Collection and delivery';
  const pageLead = isRemovalsFlow
    ? 'Where you are moving from and to, and the day you would like it to happen.'
    : storageRequired
      ? 'Tell us where to collect from and when. The return address is optional — you can give us that whenever you are ready for your things back.'
      : 'Tell us where to collect from, when, and where it is all going.';

  return (
    <>
      <NavigationStatus
        step={isRemovalsFlow ? 4 : 2}
        locationState={history.location.state}
      />

      <div className="cd-page">
        <header className="cd-page__intro">
          <Title>{pageTitle}</Title>
          <p className="cd-page__lead">{pageLead}</p>
        </header>

        <form className="cd-page__form" onSubmit={handleSubmit} noValidate>
          <div className="cd-page__legs">
            {isRemovalsFlow ? (
              <>
                <BookingLeg
                  step={1}
                  id="cd-from"
                  title="Moving from"
                  lead="Where the move starts. We currently cover London, Exeter, Sheffield, Cambridge and Essex."
                  status={isCollectionValid ? 'complete' : null}
                >
                  <AddressLookup
                    address={collectionAddress}
                    setAddress={setCollectionAddress}
                    onlyUnitedKingdom
                    idPrefix="collection"
                    label="Address you are moving from"
                    fieldErrors={collectionErrors}
                    allowedCities={REMOVALS_SERVICE_CITIES}
                  />
                </BookingLeg>

                <BookingLeg
                  step={2}
                  id="cd-to"
                  title="Moving to"
                  lead={
                    wantsStorage
                      ? 'Where your belongings go after storage.'
                      : 'Where your belongings are going. We need this to price the move.'
                  }
                  status={
                    dontKnowDeliveryAddress
                      ? 'optional'
                      : isDeliveryValid && deliveryStarted
                        ? 'complete'
                        : null
                  }
                  choice={
                    // Only the storage variant of a removal may be booked
                    // without a destination — a plain move has to be priced
                    // against one.
                    wantsStorage
                      ? {
                          name: 'cd-destination',
                          checked: !dontKnowDeliveryAddress,
                          onChange: (checked: boolean) =>
                            setDontKnowDeliveryAddress(!checked),
                          onLabel: 'I know where I am moving to',
                          onDescription: 'Enter the address now.',
                          offLabel: 'I will decide later',
                          offDescription:
                            'We will ask when you are ready for your belongings back. The price shown covers a move within the same city.',
                        }
                      : undefined
                  }
                >
                  <AddressLookup
                    address={deliveryAddress}
                    setAddress={setDeliveryAddress}
                    onlyUnitedKingdom
                    idPrefix="delivery"
                    label="Address you are moving to"
                    fieldErrors={deliveryErrors}
                    allowedCities={REMOVALS_SERVICE_CITIES}
                  />

                  {wantsStorage && deliveryAddress.postCode && (
                    <Callout tone="info" title="Return delivery fee">
                      <p>
                        A delivery charge equal to your collection charge is
                        taken five days before the return date. A return to a
                        different city is priced separately.
                      </p>
                    </Callout>
                  )}
                </BookingLeg>

                {differentCityError && (
                  <div className="cd-page__notice">
                    <Callout tone="warning" live title="This needs a quote">
                      <p>{differentCityError}</p>
                    </Callout>
                  </div>
                )}

                <div className="cd-page__leg--wide">
                  <BookingLeg
                    step={3}
                    id="cd-move-date"
                    title="Move date"
                    lead="Tell us the day you would like to move. We will confirm it with you — it is not guaranteed until we do."
                    status={moveDate && isMoveDateValid ? 'complete' : null}
                  >
                    <DateField
                      id="moveDate"
                      // Not "Move date": that is the leg's own heading, and a
                      // field sharing its section's name reads as a duplicate
                      // to a screen reader. "Preferred" is also the truth —
                      // the lead says we confirm it.
                      label="Preferred date"
                      value={moveDate}
                      onChange={(day) => setMoveDate(day)}
                      disabledDays={moveDisabledDays}
                      onValidationError={setMoveDateError}
                      error={shown(moveDateIssue)}
                      hint={
                        isMiniOrSmallTier
                          ? 'Weekdays, plus Saturdays for Mini and Small packages in Cambridge and London. Bank holidays are not available.'
                          : 'Weekdays only. Bank holidays and past dates are not available.'
                      }
                    />

                    <Callout tone="warning" title="Weekend surcharges">
                      <ul>
                        <li>Friday moves carry a surcharge</li>
                        <li>Saturday moves carry a surcharge</li>
                      </ul>
                    </Callout>
                  </BookingLeg>
                </div>

                {selectedTier && (
                  <div className="cd-page__notice">
                    <section className="cd-page__terms">
                      <div className="cd-page__terms-head">
                        <h2 className="cd-page__terms-title">
                          {selectedTier.name}
                        </h2>
                        <span className="cd-page__terms-price">
                          {toMoney(selectedTier.price)}
                        </span>
                      </div>
                      {selectedTier.description && (
                        <p className="cd-page__terms-detail">
                          {selectedTier.description}
                        </p>
                      )}
                      {selectedTier.details && (
                        <p className="cd-page__terms-detail">
                          {selectedTier.details}
                        </p>
                      )}

                      {wantsStorage && (
                        <Callout tone="info" title="Returning from storage">
                          <ul>
                            <li>
                              The return delivery charge equals your collection
                              charge ({toMoney(selectedTier.price)})
                            </li>
                            <li>
                              It applies within a 25-mile radius, or the same
                              city
                            </li>
                            <li>A return to a different city is quoted separately</li>
                            <li>
                              We need at least 7 working days&rsquo; notice
                            </li>
                            <li>
                              The charge is taken 5 days before the return date
                            </li>
                          </ul>
                        </Callout>
                      )}

                      <Callout tone="warning" title="Terms of this booking">
                        <ul>
                          <li>
                            <strong>Time.</strong> The price covers the hours in
                            the package you chose. Time beyond that is charged
                            separately, with a minimum of {toMoney(35000)}.
                          </li>
                          <li>
                            <strong>Space.</strong> The price covers the space in
                            the package you chose. If the move is larger, we pack
                            what was booked and you can rebook for the rest.
                          </li>
                          <li>
                            <strong>Area.</strong> The price covers moves within
                            a 25-mile radius, or the same city. Longer journeys
                            are quoted separately.
                          </li>
                        </ul>
                      </Callout>
                    </section>
                  </div>
                )}
              </>
            ) : (
              <>
                <BookingLeg
                  step={1}
                  id="cd-collection"
                  title="Collection"
                  lead={
                    route === 'suitcase'
                      ? 'Where we will collect your suitcases from.'
                      : 'Where we will collect your belongings from — and where we will deliver your empty boxes and packing materials.'
                  }
                  status={isCollectionValid ? 'complete' : null}
                >
                  <AddressLookup
                    address={collectionAddress}
                    setAddress={setCollectionAddress}
                    onlyUnitedKingdom
                    idPrefix="collection"
                    label="Collection address"
                    fieldErrors={collectionErrors}
                  >
                    {storageRequired &&
                      collectionAddress.postCode &&
                      isOutOfArea(collectionAddress.postCode) && (
                        <Callout tone="warning">
                          <p>
                            There is a surcharge of{' '}
                            {toMoney(COLLECTION_SURCHARGE)} for collections in
                            this area.
                          </p>
                        </Callout>
                      )}
                  </AddressLookup>

                  <DateField
                    id="collectionDate"
                    label="Collection date"
                    value={collectionDate}
                    onChange={(day) => setCollectionDate(day)}
                    disabledDays={collectionDisabledDays}
                    onValidationError={setCollectionDateError}
                    error={shown(collectionDateIssue)}
                    hint="Weekdays only. Bank holidays and past dates are not available."
                  />

                  <Callout tone="note">
                    <p>
                      Please have everything on the ground floor and ready to go
                      on the day.
                    </p>
                  </Callout>
                </BookingLeg>

                {storageRequired ? (
                  <BookingLeg
                    step={2}
                    id="cd-return"
                    title="Return"
                    lead="Where your belongings come back to. Nothing here is needed to book — you can tell us whenever you are ready for them."
                    status={returnProvided && isDeliveryValid ? 'complete' : 'optional'}
                    choice={{
                      name: 'cd-return',
                      checked: isReturnOn,
                      onChange: setIsReturnOn,
                      onLabel: 'I know the return address',
                      onDescription:
                        'Enter it now and we will keep it on your order.',
                      offLabel: 'Tell you later',
                      offDescription:
                        'We will ask when you order your boxes back. There is no rush, and it will not hold up your booking.',
                    }}
                  >
                    <AddressLookup
                      address={deliveryAddress}
                      setAddress={setDeliveryAddress}
                      onlyUnitedKingdom
                      idPrefix="delivery"
                      label="Return address"
                      fieldErrors={deliveryErrors}
                    >
                      {deliveryAddress.postCode &&
                        collectionAddress.postCode &&
                        isDifferentArea(
                          deliveryAddress.postCode,
                          collectionAddress.postCode,
                        ) && (
                          <Callout tone="warning">
                            <p>
                              Returning to a different area carries a surcharge
                              of {toMoney(REDELIVER_SURCHARGE)} per box.
                            </p>
                          </Callout>
                        )}
                    </AddressLookup>

                    <DateField
                      id="deliveryDate"
                      label="Return date"
                      optional
                      value={deliveryDate}
                      onChange={(day) => setDeliveryDate(day)}
                      disabledDays={deliveryDisabledDays}
                      onValidationError={setDeliveryDateError}
                      error={shown(deliveryDateIssue)}
                      hint={`Weekdays only, and at least ${MIN_STORAGE_WORKING_DAYS} working days after your collection date. You can also order your boxes back at any time from your dashboard.`}
                    />
                  </BookingLeg>
                ) : (
                  <BookingLeg
                    step={2}
                    id="cd-delivery"
                    title="Delivery"
                    lead="Where your belongings are going."
                    status={isDeliveryValid && deliveryStarted ? 'complete' : null}
                  >
                    <AddressLookup
                      address={deliveryAddress}
                      setAddress={setDeliveryAddress}
                      onlyUnitedKingdom
                      idPrefix="delivery"
                      label="Delivery address"
                      fieldErrors={deliveryErrors}
                    />
                  </BookingLeg>
                )}
              </>
            )}
          </div>

          <BookingSummary
            figures={figures}
            statusId="cd-summary-status"
            status={status}
            statusTone={submitAttempted && !isValid ? 'alert' : 'neutral'}
            action={
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={isSubmitting}
                rightIcon={<SummaryArrow />}
                aria-describedby="cd-summary-status"
              >
                Next
              </Button>
            }
            notes={
              storageRequired ? (
                <>
                  Collection {toMoney(COLLECTION_FEE)}, plus{' '}
                  {toMoney(COLLECTION_SURCHARGE)} in some areas. Redelivery{' '}
                  {toMoney(REDELIVER_FEE)} a box at the end of storage, plus{' '}
                  {toMoney(REDELIVER_SURCHARGE)} a box to a different city. One
                  month minimum storage charge.
                </>
              ) : undefined
            }
          />
        </form>
      </div>
    </>
  );
}

export default withRouter(CollectionDeliveryComponent);
